import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { post } from '../api'
import { useSession } from '../session'
import { t } from '../i18n'
import { BILLING_PATH, hasPlan } from '../billing'

/** The account menu in the header: the email, Plan & usage (BILL-B5a; not in the box), Devices & apps (docs/bridge-auth.md §5), API tokens (docs/api-tokens.md §8.1), Sign out. */
export function AccountMenu() {
  const { user } = useSession()
  const qc = useQueryClient()
  const nav = useNavigate()
  const [open, setOpen] = useState(false)
  const btn = useRef<HTMLButtonElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  const close = useCallback((focus = true) => {
    setOpen(false)
    if (focus) btn.current?.focus()
  }, [])

  useEffect(() => {
    if (!open) return
    menu.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
    const down = (e: PointerEvent) => {
      const el = e.target as Node
      if (menu.current?.contains(el) || btn.current?.contains(el)) return
      close(false)
    }
    document.addEventListener('pointerdown', down)
    return () => document.removeEventListener('pointerdown', down)
  }, [open, close])

  const onKey = (e: ReactKeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      close()
      return
    }
    if (e.key === 'Tab') return close(false)
    const list = Array.from(menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])
    const i = list.indexOf(document.activeElement as HTMLElement)
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      const n = e.key === 'ArrowDown' ? i + 1 : i - 1
      list[(n + list.length) % list.length]?.focus()
    }
  }

  const item = 'block w-full rounded-md px-3 py-2 text-left text-sm text-text-soft no-underline hover:bg-slot hover:text-text focus:bg-slot focus:text-text focus:outline-none'
  return (
    <span className="relative flex">
      <button
        ref={btn}
        type="button"
        data-testid="account-button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? 'account-menu' : undefined}
        aria-label={t('account.menu')}
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex min-h-[44px] min-w-[44px] max-w-[260px] items-center justify-center gap-2 rounded-md border text-sm font-medium md:min-h-9 md:px-3 ${
          open ? 'border-accent text-text' : 'border-line-2 text-text-soft hover:text-text'
        }`}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden className="flex-none">
          <circle cx="8" cy="5.5" r="2.75" stroke="currentColor" strokeWidth="1.5" />
          <path d="M2.75 13.5c.6-2.4 2.7-3.75 5.25-3.75s4.65 1.35 5.25 3.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <span className="hidden truncate md:inline">{user?.email}</span>
      </button>
      {open && (
        <div
          ref={menu}
          id="account-menu"
          role="menu"
          aria-label={t('account.menu')}
          data-testid="account-menu"
          onKeyDown={onKey}
          className="absolute right-0 top-[calc(100%+8px)] z-30 flex w-64 max-w-[calc(100vw-32px)] flex-col gap-0.5 rounded-lg border border-accent bg-panel p-2 shadow-pop"
        >
          <p className="truncate px-3 pt-1 pb-2 text-xs text-muted" title={user?.email}>
            {user?.email}
          </p>
          {hasPlan(user) && (
            <Link role="menuitem" to={BILLING_PATH} data-testid="account-billing" className={item} onClick={() => close(false)}>
              {t('account.billing')}
            </Link>
          )}
          <Link role="menuitem" to="/account/devices" className={item} onClick={() => close(false)}>
            {t('account.devices')}
          </Link>
          <Link role="menuitem" to="/account/api-tokens" className={item} onClick={() => close(false)}>
            {t('account.apiTokens')}
          </Link>
          <button
            role="menuitem"
            type="button"
            className={item}
            onClick={async () => {
              await post('/api/auth/logout').catch(() => undefined)
              qc.clear()
              nav('/login')
              location.reload()
            }}
          >
            {t('account.signOut')}
          </button>
        </div>
      )}
    </span>
  )
}
